import { useState, useEffect } from 'react'
import SunEditor from 'suneditor-react'
import 'suneditor/dist/css/suneditor.min.css'
import { Button, message, Modal } from '@pankod/refine-antd'
import LatexEquationEditorModal from './latex_eqEditor_modal'
import { nhost } from 'utility'
import { compressImage } from 'helpers/question/compress-image'
import katex from 'katex'
import 'katex/dist/katex.min.css'

interface SunTextEditorProps {
  id: any
  initialValue?: string
  formValueSetterFn?: (content: string) => void
  hideToolBar?: boolean
}

type BlobInfo = {
  id: () => any
  name: () => string
  filename: () => string
  blob: () => Blob
  base64: () => string
  blobUri: () => string
  uri: () => string | undefined
}
type ProgressFn = (percent: number) => void
export type UploadHandler = (blobInfo: BlobInfo, progress: ProgressFn) => Promise<string>

const SunTextEditor: React.FC<SunTextEditorProps> = ({ id, initialValue, formValueSetterFn, hideToolBar }) => {
  const [loading, setLoading] = useState<boolean>(false)

  const handleImageUpload = async (imageTag: HTMLImageElement) => {
    let url = ''
    try {
      setLoading(true)
      const src = imageTag.src
      if (src.startsWith('data:')) {
        const mimeMatch = src.match(/^data:(image\/[a-zA-Z+]+);base64,/)
        const mimeType = mimeMatch ? mimeMatch[1] : ''
        if (mimeType !== 'image/png' && mimeType !== 'image/jpeg') {
          imageTag.src = url
          message.error('Only PNG and JPEG images are allowed. other formats are not supported.')
          throw new Error('Only PNG and JPEG images are allowed. SVG and other formats are not supported.')
        }

        const base64Data = src.split(',')[1]
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)

        const blob = new Blob([byteArray])

        const timestamp = new Date().getTime()
        const randomString = Math.random().toString(36).substring(2, 8)
        const fileName = `${timestamp}_${randomString}.png`

        // const file = new File([blob], fileName)
        let file = new File([blob], fileName, { type: 'image/png' })
        if (file && file.size / 1024 <= 100) {
          // const compressedImageFile = await compressImage(file)
          // if (compressedImageFile && compressedImageFile.size / 1024 < file.size / 1024) {
          //   console.log('After compress - Image size (KB):', (compressedImageFile.size / 1024).toFixed(2),'completed')
          //   file = compressedImageFile
          // }
          const { fileMetadata } = await nhost.storage.upload({ file })
          const fileId = fileMetadata?.id ?? ''
          url = await nhost.storage.getPublicUrl({ fileId })
          imageTag.src = url
        } else {
          imageTag.src = url
          message.error("Images larger than 100 KB cannot be uploaded.")
          throw new Error("Images larger than 100 KB cannot be uploaded.")
        }
        setLoading(false)
      } else {
        url = src
        setLoading(false)
      }
    } catch (err) {
      setLoading(false)
      console.log({ err })
    }

    setLoading(false)
    return Promise.resolve(url)
  }

  return (
    <div id={id}>
      {!hideToolBar && <LatexEquationEditorModal />}
      <SunEditor
        height="300"
        defaultValue={initialValue}
        autoFocus={false}
        readOnly={hideToolBar ? true : false}
        hideToolbar={hideToolBar ? true : false}
        onChange={value => {
          if (formValueSetterFn) formValueSetterFn(value)
        }}
        onImageUpload={handleImageUpload}
        setOptions={{
          katex: katex,
          buttonList: [
            ['undo', 'redo'],
            ['font', 'fontSize', 'formatBlock'],
            ['paragraphStyle', 'blockquote'],
            ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
            ['fontColor', 'hiliteColor', 'textStyle'],
            ['removeFormat', 'preview'],
            '/',
            ['outdent', 'indent'],
            ['align', 'horizontalRule', 'list', 'lineHeight'],
            ['table', 'link', 'image', 'math'],
            ['fullScreen', 'codeView'],
          ],
        }}
      />
    </div>
  )
}

export default SunTextEditor
